import styles from "./productSteps.module.scss";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import TitleWithDescription from "@/components/titleWithDescription/titleWithDescription";
import RoundedButton from "@/components/roundedButton/roundedButton";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import StepCard from "@/components/stepCard/stepCard";
import TitleWithDescriptionV2 from "../titleWithDescriptionV2/titleWithDescriptionV2";
import Image from "next/image";
import { useRouter } from "next/router";

const ProductSteps = ({
  stepsTitle,
  stepsDescription,
  productName,
  createTitle,
  customizeTitle,
  publishTitle,
  createIcon,
  customizeIcon,
  publishIcon,
  createSubTitle,
  createImage,
  createDescription,
  customizeSubTitle,
  customizeImage,
  customizeDescription,
  publishSubTitle,
  publishImage,
  publishDescription,
  buttonTitle,
  buttonUrl,
  target,
  meno = false,
}) => {
  const router = useRouter();
  const productNameSuffix = productName ? ` ${productName}` : "";
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const contentSectionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const imagesRef = useRef<(HTMLDivElement | null)[]>([]);
  const leftContentsRef = useRef<(HTMLDivElement | null)[]>([]);
  const rightContentsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const contents = contentSectionsRef.current;

    // Set initial position to center with first image
    if (imagesRef.current[0] && bgRef.current && containerRef.current) {
      const imagePos = imagesRef.current[0].getBoundingClientRect();
      const containerPos = containerRef.current.getBoundingClientRect();

      // Calculate position relative to the container
      const relativeLeft = imagePos.left - containerPos.left;
      const relativeTop = imagePos.top - containerPos.top;

      // Get the center points relative to the container
      const targetX =
        relativeLeft + imagePos.width / 2 - containerPos.width / 2;
      const targetY =
        relativeTop + imagePos.height / 2 - containerPos.height / 2;

      gsap.set(bgRef.current, {
        x: targetX,
        y: targetY,
      });
    }

    // Create scroll triggers for each content section
    contents.forEach((content, index) => {
      const image = imagesRef.current[index];
      const leftContent = leftContentsRef.current[index];
      const rightContent = rightContentsRef.current[index];

      if (image && bgRef.current && containerRef.current) {
        ScrollTrigger.create({
          trigger: content,
          start: "top center",
          end: "bottom center",
          onUpdate: (self) => {
            if (self.isActive) {
              const imagePos = image.getBoundingClientRect();
              const containerPos = containerRef.current.getBoundingClientRect();
              const bgElement = bgRef.current;

              // Calculate position relative to the container
              const relativeLeft = imagePos.left - containerPos.left;
              const relativeTop = imagePos.top - containerPos.top;

              // Get the center points relative to the container
              const targetX =
                relativeLeft + imagePos.width / 2 - containerPos.width / 2;
              const targetY =
                relativeTop + imagePos.height / 2 - containerPos.height / 2;

              gsap.to(bgElement, {
                x: targetX,
                y: targetY,
                duration: 0.8,
                ease: "power3.out",
                overwrite: "auto",
              });
            }
          },
        });
      }

      // Existing content animations
      if (leftContent && rightContent) {
        gsap.set([leftContent, rightContent], { opacity: 0, y: 50 });

        ScrollTrigger.create({
          trigger: content,
          start: "top 80%",
          end: "bottom bottom",

          onEnter: () => {
            gsap.to(leftContent, {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: "power3.out",
            });
            gsap.to(rightContent, {
              opacity: 1,
              y: 0,
              duration: 1,
              delay: 0.2,
              ease: "power3.out",
            });
          },
          onLeaveBack: () => {
            gsap.to([leftContent, rightContent], {
              opacity: 0,
              y: 50,
              duration: 1,
            });
          },
        });
      }
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [stepsTitle, stepsDescription, createTitle, customizeTitle, publishTitle]);

  return (
    <>
      <div
        className={`${styles["productSteps"]} ${meno ? styles["meno"] : ""}`}
        ref={containerRef}
      >
        <div ref={bgRef} className={styles["background-svg"]}>
          <Image
            src="/images/product-steps-bg.svg"
            alt="Product steps section background pattern"
            width={1000}
            height={1000}
            priority
          />
        </div>
        <div className={`custom-container`}>
          <div className={`inner`}>
            <TitleWithDescriptionV2
              title={stepsTitle}
              description={stepsDescription}
              light={!meno}
              refreshTrigger={router.asPath}
            />
            <div
              className={`${styles["productSteps__content-wrapper"]}`}
              ref={contentRef}
            >
              <div
                className={`${styles["productSteps__content"]}`}
                ref={(el) => {
                  contentSectionsRef.current[0] = el;
                }}
              >
                <div
                  className={`${styles["productSteps__content-left"]}`}
                  ref={(el) => {
                    leftContentsRef.current[0] = el;
                  }}
                >
                  <div
                    className={`${styles["productSteps__content-title"]} ${meno ? styles["meno"] : ""}`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: createIcon }} />
                    <h3 className={`font-size-48`}>{createTitle}</h3>
                  </div>
                  <p
                    className={`${styles["productSteps__content-subtitle"]} ${meno ? styles["meno"] : ""} font-size-24`}
                  >
                    {createSubTitle}
                  </p>
                  <div
                    className={`${styles["productSteps__content-image"]} d-none-lg d-flex-xs`}
                    ref={(el) => {
                      imagesRef.current[0] = el;
                    }}
                  >
                    <Image
                      src={createImage}
                      alt={`${createTitle}${productNameSuffix}`}
                      width={500}
                      height={500}
                    />
                  </div>
                  <div
                    dangerouslySetInnerHTML={{ __html: createDescription }}
                    className={`${styles["productSteps__content-description"]} ${meno ? styles["meno"] : ""} font-size-24`}
                  />
                </div>

                <div
                  className={`${styles["productSteps__content-right"]}`}
                  ref={(el) => {
                    rightContentsRef.current[0] = el;
                  }}
                >
                  <div
                    className={`${styles["productSteps__content-image"]} d-flex-lg d-none-xs`}
                    ref={(el) => {
                      imagesRef.current[0] = el;
                    }}
                  >
                    <Image
                      src={createImage}
                      alt={`${createTitle}${productNameSuffix}`}
                      width={500}
                      height={500}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`${styles["productSteps__content"]} ${styles["productSteps__content-reverse"]}`}
                ref={(el) => {
                  contentSectionsRef.current[1] = el;
                }}
              >
                <div
                  className={`${styles["productSteps__content-left"]}`}
                  ref={(el) => {
                    leftContentsRef.current[1] = el;
                  }}
                >
                  <div
                    className={`${styles["productSteps__content-title"]} ${meno ? styles["meno"] : ""}`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: customizeIcon }} />
                    <h3 className={`font-size-48`}>{customizeTitle}</h3>
                  </div>
                  <p
                    className={`${styles["productSteps__content-subtitle"]} ${meno ? styles["meno"] : ""} font-size-24`}
                  >
                    {customizeSubTitle}
                  </p>
                  <div
                    className={`${styles["productSteps__content-image"]} d-none-lg d-flex-xs`}
                    ref={(el) => {
                      imagesRef.current[1] = el;
                    }}
                  >
                    <Image
                      src={customizeImage}
                      alt={`${customizeTitle}${productNameSuffix}`}
                      width={500}
                      height={500}
                    />
                  </div>
                  <div
                    dangerouslySetInnerHTML={{ __html: customizeDescription }}
                    className={`${styles["productSteps__content-description"]} ${meno ? styles["meno"] : ""} font-size-24`}
                  />
                </div>

                <div
                  className={`${styles["productSteps__content-right"]}`}
                  ref={(el) => {
                    rightContentsRef.current[1] = el;
                  }}
                >
                  <div
                    className={`${styles["productSteps__content-image"]} d-flex-lg d-none-xs`}
                    ref={(el) => {
                      imagesRef.current[1] = el;
                    }}
                  >
                    <Image
                      src={customizeImage}
                      alt={`${customizeTitle}${productNameSuffix}`}
                      width={500}
                      height={500}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`${styles["productSteps__content"]}`}
                ref={(el) => {
                  contentSectionsRef.current[2] = el;
                }}
              >
                <div
                  className={`${styles["productSteps__content-left"]}`}
                  ref={(el) => {
                    leftContentsRef.current[2] = el;
                  }}
                >
                  <div
                    className={`${styles["productSteps__content-title"]} ${meno ? styles["meno"] : ""}`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: publishIcon }} />
                    <h3 className={`font-size-48`}>{publishTitle}</h3>
                  </div>
                  <p
                    className={`${styles["productSteps__content-subtitle"]} ${meno ? styles["meno"] : ""} font-size-24`}
                  >
                    {publishSubTitle}
                  </p>
                  <div
                    className={`${styles["productSteps__content-image"]} d-none-lg d-flex-xs`}
                    ref={(el) => {
                      imagesRef.current[2] = el;
                    }}
                  >
                    <Image
                      src={publishImage}
                      alt={`${publishTitle}${productNameSuffix}`}
                      width={500}
                      height={500}
                    />
                  </div>
                  <div
                    dangerouslySetInnerHTML={{ __html: publishDescription }}
                    className={`${styles["productSteps__content-description"]} ${meno ? styles["meno"] : ""} font-size-24`}
                  />
                  <div className={`${styles["productSteps__content-button"]}`}>
                    <RoundedButton
                      title={buttonTitle}
                      url={buttonUrl}
                      light={meno}
                      target={target}
                    />
                  </div>
                </div>

                <div
                  className={`${styles["productSteps__content-right"]}`}
                  ref={(el) => {
                    rightContentsRef.current[2] = el;
                  }}
                >
                  <div
                    className={`${styles["productSteps__content-image"]} d-flex-lg d-none-xs`}
                    ref={(el) => {
                      imagesRef.current[2] = el;
                    }}
                  >
                    <Image
                      src={publishImage}
                      alt={`${publishTitle}${productNameSuffix}`}
                      width={500}
                      height={500}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductSteps;
